import React, { useState, useEffect, useMemo } from 'react';
import { FiCheck, FiDollarSign, FiInfo, FiLoader, FiShield, FiTrendingUp } from 'react-icons/fi';
import { useAuthContext } from '../../contexts/AuthContext';
// Inline account ID extraction for standardized accounts
const extractAccountId = (account) => {
  if (!account) return null;
  return account.accountId || account.accountNumber || account.account_number || account.id || null;
};

// Normalize accounts to standardized format regardless of broker
const normalizeAccountFormat = (accounts, broker) => {
  if (!accounts || !Array.isArray(accounts)) return [];

  return accounts.map(account => {
    // If already in standardized format, return as-is
    if (account.accountId && account.accountNumber) {
      return account;
    }

    // Handle different broker formats
    switch (broker) {
      case 'tradier':
        return {
          accountId: account.account_number || account.accountId,
          accountNumber: account.account_number || account.accountNumber,
          accountNickname: account.account_number || account.accountNickname,
          accountType: 'live',
          isDayTrader: account.day_trader || false,
          status: account.status || 'active',
          openedAt: account.date_created,
          rawAccountData: account
        };

      case 'tastytrade':
        return {
          accountId: account['account-number'] || account.account_number || account.accountId,
          accountNumber: account['account-number'] || account.account_number || account.accountNumber,
          accountNickname: account.nickname || account['account-type-name'] || account.accountNickname,
          accountType: 'live',
          isDayTrader: account['day-trader-status'] || account.day_trader_status || false,
          status: account['is-closed'] ? 'closed' : 'active',
          openedAt: account['opened-at'] || account.opened_at,
          rawAccountData: account
        };

      default:
        // Generic format - try to extract common fields
        return {
          accountId: extractAccountId(account),
          accountNumber: account.accountNumber || account.account_number || extractAccountId(account),
          accountNickname: account.accountNickname || account.nickname || extractAccountId(account),
          accountType: account.accountType || 'live',
          isDayTrader: account.isDayTrader || account.day_trader || false,
          status: account.status || 'active',
          openedAt: account.openedAt || account.date_created,
          rawAccountData: account
        };
    }
  });
};

const AccountSelection = ({
  broker, // Use broker name instead of full integration object
  accounts: propAccounts, // Accept accounts as props from parent component
  onAccountSelected,
  onCancel,
  className = ''
}) => {
  const { user } = useAuthContext();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Check if we have accounts from props (from BrokerCallbackPage)
  const hasAccountsFromProps = propAccounts && Array.isArray(propAccounts) && propAccounts.length > 0;

  console.log('🔍 AccountSelection debug:', {
    broker,
    propAccounts,
    hasAccountsFromProps,
    propAccountsLength: propAccounts?.length || 0
  });

  // Use accounts from props only - no Firestore access
  useEffect(() => {
    if (!broker) {
      setLoading(false);
      return;
    }

    if (!hasAccountsFromProps) {
      setError('No accounts provided. This component must receive accounts as props.');
      setLoading(false);
      return;
    }

    console.log('✅ AccountSelection: Using accounts from props:', {
      broker,
      accountCount: propAccounts.length,
      accounts: propAccounts
    });

    // Normalize accounts format based on broker type
    const normalizedAccounts = normalizeAccountFormat(propAccounts, broker);

    console.log('🔄 AccountSelection: Normalized accounts:', normalizedAccounts);

    setAccounts(normalizedAccounts);
    setSelectedAccount(null); // User must explicitly choose
    setError(null);
    setLoading(false);
  }, [broker, hasAccountsFromProps, propAccounts]);


  const handleConfirm = async () => {
    if (!selectedAccount) {
      setError('Please select an account');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Find the selected account data using standardized format
      const selectedAccountData = accounts.find(acc => acc.accountId === selectedAccount);

      if (!selectedAccountData) {
        throw new Error('Selected account not found');
      }

      console.log('💾 Processing account selection:', {
        broker,
        accountId: selectedAccount,
        accountType: selectedAccountData.accountType
      });

      // Call parent callback - parent will handle saving via backend API
      console.log('✅ Calling parent callback to save via backend');
      if (onAccountSelected) {
        onAccountSelected(selectedAccount, selectedAccountData);
      }

    } catch (err) {
      console.error('❌ Error processing account selection:', err);
      setError('Failed to process account selection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const getAccountTypeIcon = (type) => {
    const typeStr = (type || '').toLowerCase();
    if (typeStr.includes('paper') || typeStr.includes('sandbox')) return <FiShield className="text-yellow-400" />;
    if (typeStr.includes('margin')) return <FiTrendingUp className="text-orange-400" />;
    if (typeStr.includes('cash')) return <FiDollarSign className="text-green-400" />;
    if (typeStr.includes('ira')) return <FiShield className="text-purple-400" />;
    return <FiDollarSign className="text-gray-400" />;
  };

  const formatCurrency = useMemo(() => {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return (value) => {
      if (value === null || value === undefined) return 'N/A';
      return formatter.format(value);
    };
  }, []);

  const formatDate = useMemo(() => {
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    return (dateStr) => {
      if (!dateStr) return 'N/A';
      try {
        return formatter.format(new Date(dateStr));
      } catch {
        return dateStr;
      }
    };
  }, []);

  if (!broker) {
    return (
      <div className={`w-full ${className}`}>
        <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">No broker specified</p>
        </div>
      </div>
    );
  }

  const brokerDisplayName = broker?.charAt(0).toUpperCase() + broker?.slice(1);

  return (
    <div className={`w-full ${className}`}>
      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <FiLoader className="w-8 h-8 text-blue-400 animate-spin mb-3" />
          <p className="text-gray-300">Loading accounts...</p>
        </div>
      ) : (
        <>
          {/* Accounts List */}
          <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
            {accounts.map((account) => {
              // Use standardized account structure
              const accountId = account.accountId;
              const isSelected = selectedAccount === accountId;
              const accountType = account.accountType || 'live';

              // Parse balance data from rawAccountData if available
              const rawData = account.rawAccountData || {};
              const balance = rawData.balance || {};
              const cashBalance = balance?.['cash-balance'] || balance?.cash_balance ||
                                 rawData.cash_balance || rawData.cashBalance;
              const netLiquidatingValue = balance?.['net-liquidating-value'] || balance?.net_liquidating_value;

              return (
                <div
                  key={accountId}
                  onClick={() => setSelectedAccount(accountId)}
                  className={`
                    relative p-4 rounded-lg border cursor-pointer transition-all
                    ${isSelected
                      ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800'
                    }
                  `}
                >
                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className="absolute top-4 right-4">
                      <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                        <FiCheck className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  )}

                  <div className="flex items-start gap-3">
                    {/* Account Type Icon */}
                    <div className="mt-1">
                      {getAccountTypeIcon(accountType)}
                    </div>

                    {/* Account Details */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-white">
                          {account.accountNickname || accountType}
                        </span>
                        <span className="text-xs text-gray-400">
                          ({accountId})
                        </span>
                        {(account.status === 'active' || rawData.status === 'active') && (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-300 font-medium rounded-full">
                            Active
                          </span>
                        )}
                        {accountType && (
                          <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 font-medium rounded-full">
                            {accountType.toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-gray-400">Type:</span>
                          <span className="text-white">{accountType}</span>
                        </div>

                        {(cashBalance !== undefined && cashBalance !== null) && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-gray-400">Cash:</span>
                            <span className="text-green-400 font-medium">{formatCurrency(cashBalance)}</span>
                          </div>
                        )}

                        {(netLiquidatingValue !== undefined && netLiquidatingValue !== null) && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-gray-400">Net Liq:</span>
                            <span className="text-blue-400 font-medium">{formatCurrency(netLiquidatingValue)}</span>
                          </div>
                        )}

                        {account.openedAt && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-gray-400">Opened:</span>
                            <span className="text-white">{formatDate(account.openedAt)}</span>
                          </div>
                        )}

                        {account.isDayTrader !== undefined && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium text-gray-400">Day Trade:</span>
                            <span className={
                              account.isDayTrader
                                ? 'text-green-400 font-medium'
                                : 'text-gray-400'
                            }>
                              {account.isDayTrader ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Additional Account Info from raw data */}
                      {rawData.suitable_options_level && (
                        <div className="mt-2 text-xs">
                          <span className="text-gray-400">Options Level:</span> <span className="text-blue-400 font-medium">{rawData.suitable_options_level}</span>
                        </div>
                      )}

                      {rawData.is_futures_approved && (
                        <div className="mt-1 text-xs">
                          <span className="text-gray-400">Futures:</span> <span className="text-green-400 font-medium">Approved</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info Message */}
          <div className="flex items-start gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-6">
            <FiInfo className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-200">
              You can change your account selection later from the broker integrations page.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {onCancel && (
              <button
                onClick={onCancel}
                disabled={saving}
                className="flex-1 px-4 py-2 border border-gray-600 text-white bg-gray-800 rounded-lg hover:bg-gray-700 hover:border-gray-500 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={!selectedAccount || saving}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Confirm Selection'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AccountSelection;